import React from 'react';
import { Alert, Chip, Stack, Typography } from '@mui/material';
import { Trans } from 'react-i18next';
import useBundleTranslation from 'i18n';
import type { ITestSession } from 'api/concierge';

/** "A", "A and B", "A, B and C" — the spec's list format for the live-answer note. */
export function formatReportList(names: string[]): string {
    if (names.length <= 1) return names[0] ?? '';
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export default function TestSessionBanner({ session }: { session: ITestSession }) {
    const { t } = useBundleTranslation(['app/concierge/widget']);

    // A Domain Editor preview is pinned to one domain and has no semantic model, so it states the
    // two things the steward needs to read off the banner: which domain, and why the answer here
    // may differ from what everyone else sees (switched off, or not yet in the knowledge graph).
    if (session.scope_kind === 'domain') {
        const domainName = session.domain_name ?? '';
        return (
            <Alert severity="info" icon={false} data-test="concierge_test_session_banner" sx={{ m: 1, flexShrink: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '13px', mb: 0.5 }}>
                    {t('domain_preview.banner_title', { domain: domainName })}
                </Typography>
                <Typography sx={{ fontSize: '12px', mb: 0.5 }}>{t('domain_preview.body')}</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <Chip
                        size="small"
                        label={t('domain_preview.chip_scope', { domain: domainName })}
                        data-test="concierge_test_session_chip_scope"
                    />
                    {session.domain_enabled === false && (
                        <Chip
                            size="small"
                            color="warning"
                            label={t('domain_preview.chip_disabled')}
                            data-test="concierge_test_session_chip_disabled"
                        />
                    )}
                    {session.domain_kg_status !== undefined && session.domain_kg_status !== 'available' && (
                        <Chip
                            size="small"
                            color="warning"
                            label={t('domain_preview.chip_not_published')}
                            data-test="concierge_test_session_chip_unpublished"
                        />
                    )}
                </Stack>
            </Alert>
        );
    }

    const liveNote =
        session.live_answer_note === 'published_reports'
            ? t('test_session.live_note_published_reports', {
                  reports: formatReportList(session.published_report_names),
              })
            : t('test_session.live_note_not_published');

    return (
        <Alert severity="info" icon={false} data-test="concierge_test_session_banner" sx={{ m: 1, flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '13px', mb: 0.5 }}>
                {t('test_session.banner_title', { model: session.model_name })}
            </Typography>
            <Typography sx={{ fontSize: '12px', mb: 0.5 }}>
                {session.model_published ? (
                    t('test_session.body_published')
                ) : (
                    <Trans t={t} i18nKey="test_session.body_draft" components={{ bold: <strong /> }} />
                )}{' '}
                {liveNote}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Chip
                    size="small"
                    label={t('test_session.chip_scope', { model: session.model_name })}
                    data-test="concierge_test_session_chip_scope"
                />
                {session.includes_unpublished && (
                    <Chip
                        size="small"
                        color="warning"
                        label={t('test_session.chip_unpublished')}
                        data-test="concierge_test_session_chip_unpublished"
                    />
                )}
                <Chip
                    size="small"
                    label={t('test_session.chip_session', { name: session.name })}
                    data-test="concierge_test_session_chip_session"
                />
            </Stack>
        </Alert>
    );
}
